/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CDNCacheOnloadMutation } from '@/components/content/CDNCacheOnLoadMutaion';
import { CustomerService } from '@/components/content/CustomerService';
import { Notifications } from '@/components/content/Notifications';
import { SelectedStoreSynchronizer } from '@/components/content/SelectedStoreSynchronizer';
import { SessionErrorDialog } from '@/components/content/SessionErrorDialog';
import { layoutManifest } from '@/components/layouts/manifest';
import { Meta } from '@/data/Meta';
import { useStaticSettings } from '@/data/Settings';
import { SettingProvider } from '@/data/context/setting';
import { Layout } from '@/data/types/Layout';
import { ThemeSettingsProvider } from '@/styles/theme';
import { CssBaseline, SxProps } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import Head from 'next/head';
import { FC, useEffect, useMemo } from 'react';
import { useSettings } from '@/data/Settings';
import { usePathname } from 'next/navigation';
import { loadExternalScripts, removeScripts } from '@/utils/loadExternalScripts';
import { getPageLoadScripts } from '@/utils/getPageLoadScripts';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { PAGE_TOKEN_NAME_CATEGORY } from '@/data/constants/layout';

type PageProps = {
	meta: Meta;
	layout: Layout;
	theme: Theme;
	additives?: { [key: string]: SxProps | ((theme: Theme) => SxProps) };
};

export const PageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const currentPath = usePathname();
	const { data } = usePageDataFromId();
	const  {categoryCanonicalPath} = useCatalogEntryList(data?.tokenName===PAGE_TOKEN_NAME_CATEGORY?data?.tokenValue:'')
	const { settings } = useSettings();
	const {
		ifKlarnaexists,
		siteLinkDomainName
	} = useMemo(()=>{
		return {
			ifKlarnaexists:settings?.userData.GroupOtherPaymentTypes?.includes('KLARNA'),
			siteLinkDomainName: settings.userData.SiteLink_DomainName
		}
	},[settings]);
	
	const scripts = useMemo(()=>getPageLoadScripts({ifKlarnaexists,settings}),[ifKlarnaexists,settings])

	useEffect(() => {
		if (currentPath)loadExternalScripts(scripts);
		return () => {removeScripts(scripts)}
	}, [currentPath]);
	
	const Layout = layout && layoutManifest[layout.name] ? layoutManifest[layout.name] : () => null;
	const themeAdditives = useMemo(() => ({ additives }), [additives]);
	return (
		<>
			<Head>
				<title>{meta?.title}</title>
				<meta name="title" content={meta?.title} /> 
				<meta name="description" content={meta?.description} />
				<meta name="keywords" content={meta?.keywords} />
				<meta name="theme-color" content="navbar.primary"/>
				<link rel="icon" href={`${siteLinkDomainName}/favicon.ico`} />
				<link rel="apple-touch-icon" href={`${siteLinkDomainName}/apple-touch-icon.png`} />
				{meta.canonical ? <link rel="canonical" href={`${siteLinkDomainName}${categoryCanonicalPath??meta.canonical}`} /> : null}
				{meta.robotsExcludeNoFollowPage ? <meta name="robots" content="noindex, nofollow" /> : null}
				{meta.robotsExcludeFollowPage ? <meta name="robots" content="noindex, follow" /> : null}
				<meta name="viewport" content="width=device-width,initial-scale=1"/>
				<meta name="format-detection" content="telephone=no"/>
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/Effra_W_Rg.woff`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/Chank_Chaloops_Bold.otf`} as="font" crossOrigin="anonymous" />
				<link rel="preload" href={`${process.env.NEXT_PUBLIC_ITNL_STORE_CDN_URL}/wchcontent/fonts/Chank_Chaloops_Medium.ttf`} as="font" crossOrigin="anonymous" />
			</Head>
			<CustomerService />
			<CDNCacheOnloadMutation />
			<SelectedStoreSynchronizer />
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ThemeSettingsProvider value={themeAdditives}>
					<Layout layout={layout} />
					<Notifications />
					<SessionErrorDialog />
				</ThemeSettingsProvider>
			</ThemeProvider>
		</>
	);
};

export const dynamic = 'force-static';

export const StaticPageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const { settings } = useStaticSettings();

	return settings ? (
		<SettingProvider value={settings}>
			<PageBlock meta={meta} layout={layout} theme={theme} additives={additives} />
		</SettingProvider>
	) : null;
};
